import React from 'react';

import { useIntl } from 'react-intl';
import { queryClient } from 'src/lib/react-query';
import ConfirmationModal from 'src/registry/components/blocks/ConfirmationModal';
import { DataTableNewEntryProps } from 'src/registry/components/table/types';
import { generalErrorMessages } from 'src/registry/utils/messages';
import { ICON_MAP } from 'src/utils/constants';
import { toast } from 'src/hooks/useToast';
import { useDeleteAccountingHeader } from 'src/features/accountingHeaders/api/useDeleteAccountingHeader';
import { LIST_ACCOUNTING_HEADINGS_RESOURCE } from 'src/features/accountingHeaders/api/useListAccountingHeadings';
import { AccountingHeading } from 'src/features/accountingHeaders/utils/types';
import { accountingHeadersMessages } from 'src/features/accountingHeaders/utils/messages';

interface DeleteAccountingHeaderFormModalProps<TData> extends DataTableNewEntryProps<TData> {
    selectedAccountingHeader: AccountingHeading;
    children?: React.ReactNode;
}

function DeleteAccountingHeaderFormModal<TData>({
                                              selectedAccountingHeader,
                                              children,
                                          }: DeleteAccountingHeaderFormModalProps<TData>) {
    const [isModalOpen, setIsModalOpen] = React.useState(false);
    const { formatMessage } = useIntl();

    const { mutate: deletePayStation } = useDeleteAccountingHeader({
        onSuccess: () => {
            void queryClient.invalidateQueries({ queryKey: [LIST_ACCOUNTING_HEADINGS_RESOURCE] });
            setIsModalOpen(false);
            toast({
                title: formatMessage(accountingHeadersMessages.deleteAccountingHeaderSuccess),
                description: formatMessage(accountingHeadersMessages.deleteAccountingHeaderSuccessDescription),
            });
        },
        onError: (error) => {
            if (error?.response?.status !== 400) return;
            const errorDetails = error.response?.data?.errors[0]?.detail;
            const errorCode = formatMessage(generalErrorMessages.somethingWentWrong);
            const errorMessage = errorDetails || formatMessage(generalErrorMessages.unknownError);

            toast({
                title: errorCode,
                description: errorMessage,
                variant: 'destructive',
            });
        },
    });

    return (
        <ConfirmationModal
            isModalOpen={isModalOpen}
            setIsModalOpen={setIsModalOpen}
            dialogDetails={{
                title: { ...accountingHeadersMessages.deleteAccountingHeader },
                subTitle: {
                    ...accountingHeadersMessages.deleteAccountingHeaderSubtitle,
                    values: {
                        entity: selectedAccountingHeader.name
                    }
            },
                icon: ICON_MAP.DELETE,
                confirmationMessage: { ...accountingHeadersMessages.confirmDeleteAccountingHeader},
            }}
            callbackButton={{
                variant: 'destructiveLight',
                buttonTitle: accountingHeadersMessages.deleteAccountingHeader,
                callbackFn: () => {
                    return (
                        deletePayStation({
                            id: selectedAccountingHeader.id,
                        }),
                            setIsModalOpen(false)
                    );
                },
            }}
            children={children}
        />
    );
}

export default DeleteAccountingHeaderFormModal;
